import {
   ArrowLeftMdIcon,
   Building01Icon,
   ChevronRightIcon,
   MapPinIcon,
} from '@forwardflow/icons'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { IBusinessDetails } from '@forwardflow/types/onboarding'
import {
   Button,
   ContentCard,
   Grid,
   Group,
   Radio,
   Select,
   Stack,
   TextInput,
} from '@forwardflow/ui-v2'
import { motion } from 'framer-motion'
import { useMemo, useState } from 'react'

export const BusinessDetails = () => {
   const { getNextStep, getPreviousStep, onboardingData, setBusinessDetails } =
      useOnboardingStoreV2()
   const { businessDetails } = onboardingData

   const [values, setValues] = useState<Partial<IBusinessDetails>>({
      legalBusinessName: businessDetails?.legalBusinessName || '',
      businessType: businessDetails?.businessType || '',
      businessOwnerName: businessDetails?.businessOwnerName || '',
      businessOwnerTitle: businessDetails?.businessOwnerTitle || '',
      phoneNumber: businessDetails?.phoneNumber || '',
      email: businessDetails?.email || '',
      websiteUrl: businessDetails?.websiteUrl || '',
      businessEIIN: businessDetails?.businessEIIN || '',
      street: businessDetails?.street || '',
      city: businessDetails?.city || '',
      state: businessDetails?.state || '',
      postalCode: businessDetails?.postalCode || '',
      businessTeamName: businessDetails?.businessTeamName || '',
      businessBrokerageName: businessDetails?.businessBrokerageName || '',
      hasTeam: businessDetails?.hasTeam || false,
   })

   const [errors, setErrors] = useState<Partial<Record<keyof IBusinessDetails, string>>>({})

   const validateBusinessOwnerName = (name: string): string => {
      if (!name) return ''
      const nameRegex = /^[a-zA-Z\s.]+$/
      if (!nameRegex.test(name)) {
         return 'Business Owner Name can only consist alphabets, period or space.'
      }
      return ''
   }

   const validateWebsiteUrl = (url: string): string => {
      if (!url) return ''
      if (url.includes(' ')) {
         return 'Enter a valid website URL.'
      }
      // Must have format xxxx.xx (domain.extension)
      const urlRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w- ./?%&=]*)?$/
      if (!urlRegex.test(url)) {
         return 'Enter a valid website URL.'
      }
      return ''
   }

   const validateEIN = (ein: string): string => {
      if (!ein) return ''
      const cleanEIN = ein.replace(/[-\s]/g, '')
      if (!/^\d+$/.test(cleanEIN)) {
         return 'Enter a valid 9-digit EIN.'
      }
      if (cleanEIN.length !== 9) {
         return 'Enter a valid 9-digit EIN.'
      }
      return ''
   }

   const validatePostalCode = (zip: string): string => {
      if (!zip) return ''
      if (!/^\d+$/.test(zip)) {
         return 'ZIP should only be numbers, and 5 digits.'
      }
      if (zip.length !== 5) {
         return 'ZIP should only be numbers, and 5 digits.'
      }
      return ''
   }

   const validateEmail = (email: string): string => {
      if (!email) return ''
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email)) {
         return 'Enter a valid email address.'
      }
      return ''
   }

   const validatePhoneNumber = (phone: string): string => {
      if (!phone) return ''
      const cleanPhone = phone.replace(/[\s\-()]/g, '')
      if (!/^\d+$/.test(cleanPhone)) {
         return 'Phone number should only contain digits.'
      }
      if (cleanPhone.length !== 10) {
         return 'Enter a valid 10-digit phone number.'
      }
      return ''
   }

   const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target
      setValues({ ...values, [name]: value })

      // Validate on change
      let error = ''
      if (name === 'businessOwnerName') {
         error = validateBusinessOwnerName(value)
      } else if (name === 'websiteUrl') {
         error = validateWebsiteUrl(value)
      } else if (name === 'businessEIIN') {
         error = validateEIN(value)
      } else if (name === 'postalCode') {
         error = validatePostalCode(value)
      } else if (name === 'email') {
         error = validateEmail(value)
      } else if (name === 'phoneNumber') {
         error = validatePhoneNumber(value)
      }

      setErrors({ ...errors, [name]: error })
   }

   const isValid = useMemo(() => {
      const hasAllRequiredFields =
         values.legalBusinessName &&
         values.businessType &&
         values.businessOwnerName &&
         values.businessOwnerTitle &&
         values.phoneNumber &&
         values.email &&
         values.websiteUrl &&
         values.businessEIIN &&
         values.street &&
         values.city &&
         values.state &&
         values.postalCode &&
         values.businessBrokerageName

      const hasNoErrors = !Object.values(errors).some((error) => error !== '')

      return hasAllRequiredFields && hasNoErrors
   }, [values, errors])

   return (
      <Stack gap={48}>
         <ContentCard
            className="space-y-2"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
         >
            <p className="text-white font-medium">
               Tell us a bit about your business
            </p>
            <p className="text-[#99A1AF] text-sm font-medium max-w-[600px]">
               Share your basic business details so we can tailor your Forward
               Flow experience
            </p>
         </ContentCard>
         <Stack gap={24}>
            <ContentCard
               className="space-y-4"
               initial={{ opacity: 0, y: 30 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.3, delay: 0.6 }}
            >
               <Group>
                  <Group className="p-1.5 px-2.5 rounded-xl border-[0.9px] border-[#8E51FF]/30 bg-gradient-to-br from-[#4F39F6]/30 to-[#7F22FE]/30">
                     <Building01Icon width={20} color="#9F7BFF" />
                  </Group>
                  <p className="text-white font-medium">Business Information</p>
               </Group>

               <Grid>
                  <Grid.Col span={6}>
                     <TextInput
                        value={values.legalBusinessName}
                        label="Legal business name"
                        placeholder="Forward Flow Real Estate LLC"
                        name="legalBusinessName"
                        onChange={handleChange}
                     />
                  </Grid.Col>
                  <Grid.Col span={6}>
                     <Select
                        value={values.businessType}
                        label="Business Type"
                        placeholder="Select Business Type"
                        name="businessType"
                        onChange={(value) =>
                           setValues({ ...values, businessType: value || '' })
                        }
                        data={[
                           { label: 'Co-operative', value: 'co-operative' },
                           { label: 'Corporation', value: 'corporation' },
                           { label: 'Limited Liability Corporation', value: 'limited-liability-corporation' },
                           { label: 'Non-profit Corporation', value: 'non-profit-corporation' },
                           { label: 'Partnership', value: 'partnership' },
                        ]}
                     />
                  </Grid.Col>
               </Grid>

               <Grid>
                  <Grid.Col span={6}>
                     <TextInput
                        value={values.businessOwnerName}
                        label="Business owner"
                        placeholder="John Smith"
                        name="businessOwnerName"
                        onChange={handleChange}
                        error={errors.businessOwnerName}
                     />
                  </Grid.Col>
                  <Grid.Col span={6}>
                     <TextInput
                        value={values.businessOwnerTitle}
                        label="Your title"
                        placeholder="Agent, Broker, etc."
                        name="businessOwnerTitle"
                        onChange={handleChange}
                     />
                  </Grid.Col>
               </Grid>

               <Grid>
                  <Grid.Col span={6}>
                     <TextInput
                        value={values.phoneNumber}
                        label="Phone Number"
                        placeholder="415 232 9864"
                        name="phoneNumber"
                        onChange={handleChange}
                        error={errors.phoneNumber}
                        type="tel"
                     />
                  </Grid.Col>
                  <Grid.Col span={6}>
                     <TextInput
                        value={values.email}
                        label="Email"
                        placeholder="brannon@forward-flow.com"
                        name="email"
                        onChange={handleChange}
                        error={errors.email}
                        type="email"
                     />
                  </Grid.Col>
               </Grid>

               <Stack>
                  <p className="text-white text-sm font-medium">
                     Are you part of a Team?
                  </p>
                  <Group>
                     <Group className="rounded-lg border border-white/[0.1] bg-white/[0.02] px-4 py-2">
                        <Radio
                           name="hasTeam"
                           value="yes"
                           checked={values.hasTeam}
                           onChange={() =>
                              setValues({ ...values, hasTeam: true })
                           }
                        />
                        <p className="text-white text-sm font-medium">Yes</p>
                     </Group>

                     <Group className="rounded-lg border border-white/[0.1] bg-white/[0.02] px-4 py-2">
                        <Radio
                           name="hasTeam"
                           value="no"
                           checked={!values.hasTeam}
                           onChange={() =>
                              setValues({ ...values, hasTeam: false })
                           }
                        />
                        <p className="text-white text-sm font-medium">No</p>
                     </Group>
                  </Group>
               </Stack>

               {values.hasTeam && (
                  <TextInput
                     value={values.businessTeamName}
                     label="Team name"
                     placeholder="Forward Team"
                     name="businessTeamName"
                     onChange={handleChange}
                  />
               )}

               <TextInput
                  value={values.businessBrokerageName}
                  label="Brokerage name"
                  placeholder="FF Brokerage"
                  name="businessBrokerageName"
                  onChange={handleChange}
               />

               <TextInput
                  value={values.websiteUrl}
                  label="Website"
                  placeholder="https://www.ffrealestate.com"
                  name="websiteUrl"
                  onChange={handleChange}
                  error={errors.websiteUrl}
               />

               <TextInput
                  value={values.businessEIIN}
                  label="Business EIN"
                  placeholder="12-3456789"
                  name="businessEIIN"
                  description="We need your EIN for A2P registration - it's required to send verified text messages. You can skip this for now and add it anytime later from Settings → Business Info."
                  onChange={handleChange}
                  error={errors.businessEIIN}
               />
            </ContentCard>
            <ContentCard
               className="space-y-4"
               initial={{ opacity: 0, y: 30 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.3, delay: 0.6 }}
            >
               <Group>
                  <Group className="p-1.5 px-2.5 rounded-xl border-[0.9px] border-[#8E51FF]/30 bg-gradient-to-br from-[#4F39F6]/30 to-[#7F22FE]/30">
                     <MapPinIcon width={20} color="#9F7BFF" />
                  </Group>
                  <p className="text-white font-medium">Registration address</p>
               </Group>
               <TextInput
                  value={values.street}
                  label="Street address"
                  placeholder="123 Main Street, Suite 100"
                  name="street"
                  onChange={handleChange}
                  autoComplete="street-address"
               />

               <Grid>
                  <Grid.Col span={4}>
                     <TextInput
                        value={values.city}
                        label="City"
                        placeholder="San Francisco"
                        name="city"
                        onChange={handleChange}
                        autoComplete="address-level2"
                     />
                  </Grid.Col>
                  <Grid.Col span={4}>
                     <TextInput
                        value={values.state}
                        label="State"
                        placeholder="CA"
                        name="state"
                        onChange={handleChange}
                        autoComplete="address-level1"
                     />
                  </Grid.Col>
                  <Grid.Col span={4}>
                     <TextInput
                        value={values.postalCode}
                        label="ZIP"
                        placeholder="94102"
                        name="postalCode"
                        onChange={handleChange}
                        autoComplete="postal-code"
                        error={errors.postalCode}
                     />
                  </Grid.Col>
               </Grid>
            </ContentCard>
         </Stack>

         <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.9 }}
         >
            <Grid>
               <Grid.Col span={3}>
                  <Button
                     variant="secondary"
                     className="w-full"
                     onClick={() => getPreviousStep()}
                  >
                     <ArrowLeftMdIcon width={12} /> Back
                  </Button>
               </Grid.Col>
               <Grid.Col span={9}>
                  <Button
                     className="w-full"
                     onClick={() => {
                        setBusinessDetails({
                           ...businessDetails,
                           ...values,
                        })
                        getNextStep()
                     }}
                     disabled={!isValid}
                  >
                     Continue <ChevronRightIcon width={18} />
                  </Button>
               </Grid.Col>
            </Grid>
         </motion.div>
      </Stack>
   )
}
